import { Injectable } from '@angular/core';
import {
  ChartDataPoint,
  DataChip,
  DataSeries,
  GraphDataPoint,
  GraphDataResponse,
  GraphDimension,
  GraphTimeSeriesData
} from '@dr-customer-offers-ui/lib-interfaces';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import moment from 'moment-timezone';
import { RegConfig } from '../../../../shared/models/reg-config';
import { OfferRowData } from './component/open-graph-table/open-graph-table.component';

/**
 * OpenGraphService
 *
 * Holds the *pure* transform + formatting logic for the Open graph:
 * API response -> chips / chart series / table rows / plot bands / capacity marks / tooltip HTML.
 *
 * Why a service: none of this needs the component's Highcharts instance, MatDialog,
 * or signals — it's input -> output. Keeping it here makes the component thin and
 * this logic unit-testable in isolation. Anything that DOES need runtime state
 * (signals, chart ref, dialog, click handlers, chart options/closures) stays in the
 * component. Values the service can't derive on its own (currency, frequency,
 * timezone, unit, regViewConfig) are passed in as parameters, so the service never
 * reaches back into component state.
 *
 * NOTE: this is a straight lift of the existing component logic — no behavioural
 * changes. The dimension model is: single dims (kW / PRICE / DEMAND_FORECAST) render
 * as base series and default missing values to 0; the four split dims
 * (KW_TO_INCREASE / KW_TO_DECREASE / PRICE_TO_INCREASE / PRICE_TO_DECREASE) are NOT
 * drawn as base series — they feed the capacity marks + tooltip, and keep null when
 * absent so those intervals are skipped rather than plotted as 0.
 */
@Injectable({ providedIn: 'root' })
export class OpenGraphService {
  /** Split (increase/decrease) dimension ids. These are carried for marks/tooltip, not drawn as base series, and keep null when absent. */
  private static readonly COMPOUND_DIM_IDS = ['KW_TO_INCREASE', 'KW_TO_DECREASE', 'PRICE_TO_INCREASE', 'PRICE_TO_DECREASE'];
  static readonly GATE_CLOSED_COLOR = '#ffe9e8';
  static readonly BASELINE_DIM_ID = 'BASELINE';

  constructor(private translate: TranslateService) {}

  shouldOverlayBaseline(regViewConfig: RegConfig | null): boolean {
    return !!regViewConfig?.graph_view && !!regViewConfig?.tab_display?.display_baselines_tab;
  }

  /** Currency is driven by the registration timezone (Warsaw -> PLN, otherwise EUR). */
  resolveCurrency(timeZone: string | undefined): string {
    return timeZone === 'Europe/Warsaw' ? 'PLN' : 'EUR';
  }

  /**
   * Whether to use the "old" consolidated tooltip + hide the capacity marks.
   * True when the config consolidates into hourly
   */
  isConsolidatedToHourly(regViewConfig: RegConfig | null): boolean {
    const consolidateDimensions = regViewConfig?.graph_config?.consolidate_dimensions_into_hourly;
    return !!consolidateDimensions;
  }

  /**
   * Whether to use the "old" consolidated tooltip + hide the capacity marks.
   * True when the config has none of
   * the split dims (a plain kW/PRICE/DEMAND program has nothing to draw marks for).
   */
  isSplitDimsAbsent(regViewConfig: RegConfig | null): boolean {
    const hasPriceDemandRange = regViewConfig?.graph_config?.offer_dimensions?.some((dim: string) => OpenGraphService.COMPOUND_DIM_IDS.includes(dim));
    return !hasPriceDemandRange;
  }

  /** Color / chart type / default visibility per series display name. Split dims are hidden base series (green up, red down). */
  getSeriesConfiguration(seriesName: string): { color: string; chartType: 'line' | 'column' | 'bar'; defaultVisible: boolean } {
    switch (seriesName) {
      case 'Demand':
        return { color: '#000000', chartType: 'line', defaultVisible: true };
      case 'Capacity':
        return { color: '#bfebbb', chartType: 'column', defaultVisible: true };
      case 'Price':
        return { color: '#FF6B6B', chartType: 'column', defaultVisible: false };
      // Compound dims are not base series — they feed the marks/tooltip, not a drawn line/bar
      case 'Capacity Increase':
      case 'Price Increase':
        return { color: '#00A879', chartType: 'line', defaultVisible: false };
      case 'Capacity Decrease':
      case 'Price Decrease':
        return { color: '#E8384F', chartType: 'line', defaultVisible: false };
      case 'Baseline':
        return { color: '#000000', chartType: 'line', defaultVisible: true };
      default:
        return { color: '#FF6B6B', chartType: 'column', defaultVisible: false };
    }
  }

  /** Series display name -> dimension key used when joining series back into table rows. */
  mapSeriesNameToDimensionKey(seriesName: string): string {
    switch (seriesName) {
      case 'Capacity Increase':
        return 'KW_TO_INCREASE';
      case 'Capacity Decrease':
        return 'KW_TO_DECREASE';
      case 'Price Increase':
        return 'PRICE_TO_INCREASE';
      case 'Price Decrease':
        return 'PRICE_TO_DECREASE';
      case 'Demand':
        return 'DEMAND_FORECAST';
      case 'Capacity':
        return 'kW';
      case 'Price':
        return 'PRICE';
      case 'Baseline':
        return 'BASELINE';
      default:
        return '';
    }
  }

  /** Build the full chip list (price unit shows currency, everything else shows the API unit). */
  buildChips(response: GraphDataResponse, currency: string, offerDimensions: string[]): DataChip[] {
    return response.timeSeriesData
      .filter((series) => !OpenGraphService.COMPOUND_DIM_IDS.includes(series.id) || offerDimensions.includes(series.id))
      .map((series: GraphTimeSeriesData) => {
        const seriesConfig = this.getSeriesConfiguration(series.name);
        const isPrice = series.name.toLowerCase().includes('price');

        return {
          id: series.id,
          label: series.name,
          selected: seriesConfig.defaultVisible,
          color: seriesConfig.color,
          unit: isPrice ? currency : response.metadata.unit || '',
          chartType: seriesConfig.chartType
        };
      });
  }

  /** Chips actually shown in the legend — price chips are hidden (price is tooltip/table only). */
  getVisibleChips(chips: DataChip[]): DataChip[] {
    return chips.filter((chip) => chip.id !== 'PRICE' && chip.id !== 'PRICE_TO_INCREASE' && chip.id !== 'PRICE_TO_DECREASE');
  }

  /**
   * Convert the API response into chart series.
   * - split dims keep null when a value is absent (so gaps aren't drawn as 0)
   * - single dims default absent values to 0
   * - a non-split series that is entirely null is dropped, but split series are always
   *   kept (they still need to exist for the marks/tooltip even if all-null today).
   */
  buildChartSeries(response: GraphDataResponse, chips: DataChip[]): DataSeries[] {
    const chartSeries = response.timeSeriesData
      .map((apiSeries: GraphTimeSeriesData) => {
        const chip = chips.find((c: DataChip) => c.id === apiSeries.id);
        const keepNull = OpenGraphService.COMPOUND_DIM_IDS.includes(apiSeries.id) || apiSeries.id === OpenGraphService.BASELINE_DIM_ID;
        if (!chip) return null;

        const data: ChartDataPoint[] = apiSeries.dataPoints
          .map(
            (point: GraphDataPoint) =>
              ({
                x: moment.utc(point.start_dttm_utc).valueOf(),
                y: point.value !== null && point.value !== undefined ? point.value : keepNull ? null : 0
              }) as ChartDataPoint
          )
          .sort((a, b) => a.x - b.x);

        return {
          id: chip.id,
          name: chip.label,
          displayName: chip.label,
          color: chip.color,
          data: data,
          unit: chip.unit,
          visible: chip.selected,
          chartType: chip.chartType
        };
      })
      .filter((series) => series !== null) as DataSeries[];

    return chartSeries.filter((s) => OpenGraphService.COMPOUND_DIM_IDS.includes(s.id) || s.data.some((p) => p.y !== null && p.y !== undefined));
  }

  /** Gray plot bands for gate-closed intervals. */
  buildPlotBands(response: GraphDataResponse): Highcharts.XAxisPlotBandsOptions[] {
    const frequency = response.metadata?.frequency ?? 60;
    const freqMs = frequency * 60000;
    return (response.timeSeriesData ?? []).flatMap((series) =>
      series.dataPoints
        .filter((p) => p.gate_closed)
        .map((p) => {
          const from = moment.utc(p.start_dttm_utc).valueOf();
          const end = moment.utc(p.end_dttm_utc).valueOf();

          return {
            from,
            // fallback if end time is missing or same as from
            to: Number.isFinite(end) && end !== from ? end : from + freqMs,
            color: OpenGraphService.GATE_CLOSED_COLOR
          };
        })
    );
  }

  /**
   * Join the chart series (keyed by dimension) back into per-interval table rows.
   * capacity.max/min <- KW_TO_INCREASE/DECREASE, price.max/min <- PRICE_TO_INCREASE/DECREASE,
   * demand <- DEMAND_FORECAST (defaulted to 0). null capacity/price stay null.
   */
  buildTableSeries(series: DataSeries[], frequency: number, currency: string): OfferRowData[] {
    const dataPointsMap = new Map<
      number,
      {
        values: {
          kW?: number;
          DEMAND_FORECAST?: number;
          PRICE?: number;
          PRICE_TO_DECREASE?: number;
          PRICE_TO_INCREASE?: number;
          KW_TO_DECREASE?: number;
          KW_TO_INCREASE?: number;
          BASELINE?: number | null;
        };
      }
    >();

    series.forEach((s) => {
      const dimensionKey = this.mapSeriesNameToDimensionKey(s.name);
      if (!dimensionKey) return;

      s.data.forEach((point) => {
        if (!dataPointsMap.has(point.x)) {
          dataPointsMap.set(point.x, { values: {} });
        }
        const entry = dataPointsMap.get(point.x)!;
        (entry.values as any)[dimensionKey] = point.y;
      });
    });

    return Array.from(dataPointsMap.entries())
      .map(([startMs, { values }]) => {
        const start = moment.utc(startMs);
        return {
          offer_start_dttm_utc: start.toISOString(),
          offer_end_dttm_utc: start.clone().add(frequency, 'minutes').toISOString(),
          demand: values.DEMAND_FORECAST ?? 0,
          baselines: values.BASELINE ?? null,
          price: {
            min: values.PRICE_TO_DECREASE,
            max: values.PRICE_TO_INCREASE,
            value: values.PRICE,
            currency
          },
          capacity: {
            min: values.KW_TO_DECREASE,
            max: values.KW_TO_INCREASE,
            value: values.kW
          }
        } as OfferRowData;
      })
      .sort((a, b) => a.offer_start_dttm_utc.localeCompare(b.offer_start_dttm_utc));
  }

  /**
   * Capacity marks: green dash = increased (capacity.max), red dash = decreased (capacity.min),
   * plus a gray column-range band filling between the two. null values plot as gaps.
   * Returns [] when there are no rows. (The consolidated/no-split guard lives in the component.)
   */
  buildCapacityMarkerSeries(rows: OfferRowData[]): any[] {
    if (!rows.length) return [];

    const toY = (v: string | number | null | undefined): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

    const increasedData = rows.map((row) => ({
      x: moment.utc(row.offer_start_dttm_utc).valueOf(),
      y: toY(row.capacity.max)
    }));

    const decreasedData = rows.map((row) => ({
      x: moment.utc(row.offer_start_dttm_utc).valueOf(),
      y: toY(row.capacity.min)
    }));

    const buildMarker = (color: string) => ({
      enabled: true,
      symbol: 'hdash',
      radius: 7,
      lineWidth: 0,
      fillColor: color,
      states: { hover: { enabled: true, radiusPlus: 1 } }
    });

    // Gray band filling the space between decreased (low) and increased (high).
    // low = capacity.min (kw_to_decrease), high = capacity.max (kw_to_increase).
    const rangeData = rows.map((row: OfferRowData) => {
      const increased = toY(row.capacity.max);
      const decreased = toY(row.capacity.min);

      let low: number | null;
      let high: number | null;

      if (increased !== null && decreased !== null) {
        low = decreased;
        high = increased;
      } else if (increased !== null) {
        low = 0;
        high = increased;
      } else if (decreased !== null) {
        low = decreased;
        high = 0;
      } else {
        low = null;
        high = null;
      }

      return {
        x: moment.utc(row.offer_start_dttm_utc).valueOf(),
        low,
        high
      };
    });

    return [
      {
        id: 'capacity-range-band',
        name: 'Capacity Range',
        type: 'columnrange',
        data: rangeData,
        color: '#bfebbb',
        pointPadding: 0.05,
        groupPadding: 0,
        borderWidth: 0,
        marker: { enabled: false },
        enableMouseTracking: false,
        pointPlacement: 'between',
        zIndex: 0,
        states: { hover: { enabled: false } }
      },
      {
        id: 'capacity-increased',
        name: 'Capacity Increased',
        type: 'scatter',
        color: '#00A879', // green
        data: increasedData,
        marker: buildMarker('#00A879'),
        zIndex: 1
      },
      {
        id: 'capacity-decreased',
        name: 'Capacity Decreased',
        type: 'scatter',
        color: '#E8384F', // red
        data: decreasedData,
        marker: buildMarker('#E8384F'),
        zIndex: 1
      }
    ];
  }

  /**
   * Grouped tooltip HTML for the split-dimension (PRT) market:
   * time pill, Demand, Capacity (↑ increased / ↓ decreased), Price (↑ / ↓).
   * `row` is the matched table row; currency falls back to the passed-in currency.
   */
  buildPrtTooltipHtml(args: {
    x: number;
    frequency: number;
    timeZone: string;
    unit: string;
    currency: string;
    row: OfferRowData | undefined;
    optedOut?: boolean;
    offerDimensions: GraphDimension[];
    showBaseline?: boolean;
  }): string {
    const { x, frequency, timeZone, unit: powerUnit, row } = args;

    const startMoment = moment.utc(x).tz(timeZone);
    const endMoment = startMoment.clone().add(frequency, 'minutes');
    const timeRange = `${startMoment.format('HH:mm')} - ${endMoment.format('HH:mm')}`;

    const fmt = (value: number | string | null | undefined, unit: string): string => {
      if (value === null || value === undefined || value === '') return '-';
      const num = Number(value);
      return Number.isNaN(num) ? `${value} ${unit}` : `${Math.round(num)} ${unit}`;
    };

    const currency = (row?.price?.currency as string) || args.currency;

    const dims = args.offerDimensions ?? [];
    const has = (d: GraphDimension): boolean => dims.includes(d);

    // Capacity and Price share the same shape: split (↑ max / ↓ min) or single (value).
    const buildGroup = (opts: {
      labelKey: [string, string];
      singleDim: GraphDimension;
      incDim: GraphDimension;
      decDim: GraphDimension;
      single: number | string | null | undefined;
      inc: number | string | null | undefined;
      dec: number | string | null | undefined;
      unit: string;
      isPrice: boolean;
    }): string => {
      const isSplit = has(opts.incDim) || has(opts.decDim);
      const isSingle = has(opts.singleDim);
      if (!isSplit && !isSingle) return '';

      // single dim
      if (!isSplit) {
        return this.prtTooltipRow(this.tr(opts.labelKey[0], opts.labelKey[1]), fmt(opts.single, opts.unit), '', false);
      }

      // split dims
      let group = this.prtTooltipGroupLabel(this.tr(opts.labelKey[0], opts.labelKey[1]));
      if (has(opts.incDim)) {
        group += this.prtTooltipRow(this.tr('COM.TABS.OPEN.CHART.INCREASED', 'Increased'), fmt(opts.inc, opts.unit), 'up', true, opts.isPrice);
      }
      if (has(opts.decDim)) {
        group += this.prtTooltipRow(this.tr('COM.TABS.OPEN.CHART.DECREASED', 'Decreased'), fmt(opts.dec, opts.unit), 'down', true, opts.isPrice);
      }
      return group;
    };

    const headerPill = `
    <div style="display:inline-block;padding:0.25rem 0.625rem 0.1rem 0;font-size:0.8125rem;color:#1f2733;font-weight:500;">
      ${timeRange}
    </div>`;

    const optOutFooter = args.optedOut
      ? `<div style="margin-top:.5rem;padding-top:.5rem;border-top:1px solid #e0e3e8;
       font-size:0.8125rem;color:#E8384F;font-weight:600;">
       ${this.tr('COM.TABS.OPEN.CHART.OFFER_OPTED_OUT', 'Offers opted out')}
     </div>`
      : '';

    const body =
      (has('DEMAND_FORECAST') ? this.prtTooltipRow(this.tr('COM.TABS.OPEN.CHART.DEMAND', 'Demand'), fmt(row?.demand, powerUnit), '', false) : '') +
      (args.showBaseline ? this.prtTooltipRow(this.tr('COM.TABS.OPEN.CHART.BASELINE', 'Baseline'), fmt(row?.baselines, powerUnit), '', false) : '') +
      buildGroup({
        labelKey: ['COM.TABS.OPEN.CHART.CAPACITY', 'Capacity'],
        singleDim: 'kW',
        incDim: 'KW_TO_INCREASE',
        decDim: 'KW_TO_DECREASE',
        single: row?.capacity?.value,
        inc: row?.capacity?.max,
        dec: row?.capacity?.min,
        unit: powerUnit,
        isPrice: false
      }) +
      buildGroup({
        labelKey: ['COM.TABS.OPEN.CHART.PRICE', 'Price'],
        singleDim: 'PRICE',
        incDim: 'PRICE_TO_INCREASE',
        decDim: 'PRICE_TO_DECREASE',
        single: row?.price?.value,
        inc: row?.price?.max,
        dec: row?.price?.min,
        unit: currency,
        isPrice: true
      });

    return `
      <div style="min-width:13rem;padding:0.25rem 0.25rem 0.125rem;font-family:Roobert, RoobertENEL-Light, sans-serif;line-height:1.4;">
        ${headerPill}
        <div style="margin-top:.5rem;padding-top:.65rem;border-top:2px solid #acb1ba;">${body}</div>
        ${optOutFooter}
      </div>`;
  }

  /** Muted group header used inside the PRT tooltip. */
  private prtTooltipGroupLabel(label: string): string {
    return `<div style="margin:0.625rem 0 0.125rem;font-size:0.8125rem;color:#8a949c;display:flex;align-items:center;"><span>${label}</span></div>`;
  }

  /** Single tooltip row: optional up/down arrow (green up / red down), label, value. */
  private prtTooltipRow(label: string, value: string, direction: 'up' | 'down' | '', indented: boolean, isPrice?: boolean): string {
    const arrowGlyph = direction === 'up' ? '&#8593;' : direction === 'down' ? '&#8595;' : '';
    const arrowColor = direction === 'up' ? (isPrice ? '#000000' : '#00A879') : direction === 'down' ? (isPrice ? '#838587' : '#E8384F') : '#5b6470';
    const arrow = arrowGlyph ? `<span style="margin-right:0.4rem;color:${arrowColor};font-size:0.8125rem;">${arrowGlyph}</span>` : '';
    return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin:0.375rem 0;${indented ? 'padding-left:0.125rem;' : ''}">
      <div style="display:flex;align-items:center;color:#1f2733;font-weight:500;">${arrow}<span>${label}</span></div>
      <div style="font-weight:700;color:#131416;text-align:right;">${value}</div>
    </div>`;
  }

  /** Translate with a fallback when the key is missing. */
  private tr(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return !value || value === key ? fallback : value;
  }

  /** Baseline as a chart-only spanning line: two points per interval (start + end) so
   *  the value holds flat across the whole [start, end], correct even for a single
   *  all-day interval; gaps break the line. Kept off the table join, hence chart-only. */
  buildBaselineLineSeries(originalApiData: GraphTimeSeriesData[], color = '#000000'): any[] {
    const baseline = originalApiData.find((s) => s.id === OpenGraphService.BASELINE_DIM_ID);
    if (!baseline?.dataPoints?.length) return [];

    const sorted = [...baseline.dataPoints].sort((a, b) => moment.utc(a.start_dttm_utc).valueOf() - moment.utc(b.start_dttm_utc).valueOf());

    const data: (ChartDataPoint | null)[] = [];
    let prevEnd: number | null = null;

    sorted.forEach((p: GraphDataPoint) => {
      const y = p.value ?? null;
      if (y === null) {
        prevEnd = null;
        return; // skip null intervals, and break continuity
      }
      const start = moment.utc(p.start_dttm_utc).valueOf();
      const end = moment.utc(p.end_dttm_utc).valueOf();

      if (prevEnd !== null && start > prevEnd) data.push(null); // gap -> break the line
      data.push({ x: start, y } as ChartDataPoint);
      data.push({ x: end, y } as ChartDataPoint);
      prevEnd = end;
    });

    return [
      {
        id: OpenGraphService.BASELINE_DIM_ID,
        name: 'Baseline',
        type: 'line',
        color,
        lineWidth: 2,
        data,
        marker: { enabled: false },
        enableMouseTracking: true,
        connectNulls: false,
        zIndex: 3
      }
    ];
  }
}
